import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Tag, Info, BarChart, X, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction, createAndEnqueueTask } from "@/utils/edgeFunctionUtils";
import { supabase } from "@/integrations/supabase/client";
import { useAppSelector } from "@/hooks/useAppSelector";
import { TaskButton } from "@/components/common/TaskButton";
import { TaskType } from "@/types/taskTypes";
import { VersionActionBar } from '@/components/common/VersionActionBar';
import { useVersion } from '@/contexts/VersionContext';
import { removeTagFromCompany } from '@/utils/tagUtils';
import { getVersions } from '@/utils/versionUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";

interface TagItem {
  id: string;
  name: string;
  description: string;
  keywords: string[] | string;
  explanation?: string;
  sources?: {
    version: string;
    url: string;
    text?: string;
  };
}

// We're no longer using this function for version display
// Keeping it for backward compatibility with other code that might use it
function formatVersionDate(versionStr: string): string {
  if (!versionStr) return '';
  return versionStr;
}

// Helper function to normalize tags from company_data view
function normalizeTag(tag: any): Tag {
  if (!tag) {
    return {
      id: 'unknown',
      name: 'Unknown',
      description: null,
      keywords: null,
      explanation: null,
      sources: null
    };
  }

  // Handle tag format from company_data view
  if (tag.tag && typeof tag.tag === 'object') {
    return {
      id: tag.tag.id || 'unknown',
      name: tag.tag.name || 'Unknown',
      description: tag.tag.description || null,
      keywords: tag.tag.keywords || null,
      explanation: tag.explanation || null,
      sources: {
        version: tag.version || '',
        url: ''
      },
      created_at: tag.created_at || null,
      version: tag.version || null,
      tag_id: tag.tag_id || null,
      page_id: tag.page_id || null
    };
  }

  // If tag is already in the right format, return it
  if (typeof tag === 'object' && tag.id && tag.name) {
    return {
      id: tag.id,
      name: tag.name,
      description: tag.description || null,
      keywords: tag.keywords || null,
      explanation: tag.explanation || null,
      sources: tag.sources || null,
      created_at: tag.created_at || null,
      version: tag.version || null,
      tag_id: tag.tag_id || null
    };
  }

  // Default case - return a placeholder
  return {
    id: 'unknown',
    name: 'Unknown',
    description: null,
    keywords: null,
    explanation: null,
    sources: null
  };
}

interface JobStatus {
  id: string;
  slug: string;
  job_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  timestamp: string;
  created_at: string;
  result?: any;
}

// Task types
type TaskType = 'EXTRACT_TAGS' | 'EXTRACT_METRICS';

interface CompanyTagsProps {
  company: any;
  pageSubslug?: string | null;
}

// Define the tag structure to match what's in the company_data view
interface Tag {
  id: string;
  name: string;
  description: string | null;
  keywords: string[] | null;
  explanation?: string | null;
  sources?: {
    url?: string;
    text?: string;
    version?: string;
  } | null;
  created_at?: string | null;
  version?: string | null;
  tag_id?: string | null;
}

// Helper function to safely extract error messages
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  } else if (error !== null && error !== undefined) {
    return String(error);
  }
  return 'Unknown error';
};

const CompanyTags = ({ company: initialCompany, pageSubslug }: CompanyTagsProps) => {
  // State for showing all tags or only most recent
  const [showAllVersions, setShowAllVersions] = useState<boolean>(false);
  // State to track the latest version with tag data
  const [latestVersionWithData, setLatestVersionWithData] = useState<string | null>(null);
  // State for extraction modal
  const [extractModalOpen, setExtractModalOpen] = useState<boolean>(false);
  // State for selected version in modal
  const [selectedExtractVersion, setSelectedExtractVersion] = useState<string | null>(null);
  // State for force flag
  const [forceExtract, setForceExtract] = useState<boolean>(false);
  // State for available versions
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);
  const [company, setCompany] = useState(initialCompany);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [lastJobTimestamp, setLastJobTimestamp] = useState<string | null>(null);
  const [lastMetricsJobTimestamp, setLastMetricsJobTimestamp] = useState<string | null>(null);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  // Add a state variable to force re-render
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();

  // Check if company is defined
  if (!company) {
    console.error('❌ [CompanyTags] Company prop is undefined');
    return (
      <div className="p-8 text-center text-gray-500">
        <p>Error: Company information is not available.</p>
        <p className="text-sm mt-2">Please try refreshing the page.</p>
      </div>
    );
  }

  // Get company tags from the company object or from versionUtils.getTags
  // Handle different possible formats
  let companyTags: Tag[] = [];

  // Get company tags from the company object
  // Handle different possible formats
  if (company.tags && Array.isArray(company.tags)) {
    // Direct tags array in company object
    companyTags = company.tags.map(normalizeTag);
  } else if (company.companyTags && Array.isArray(company.companyTags)) {
    // Tags in companyTags property
    companyTags = company.companyTags.map(normalizeTag);
  } else {
    // If no company-level tags, check if there's a main page with tags
    if (company.pages && Array.isArray(company.pages)) {
      const mainPage = company.pages.find((p: any) => !p.subslug || p.subslug === '');
      if (mainPage && mainPage.tags && Array.isArray(mainPage.tags)) {
        console.log('🔍 [CompanyTags] No company-level tags found, using main page tags');
        companyTags = mainPage.tags.map(normalizeTag);
      }
    }
  }





  // Use the normalizeTag function defined above

  // Get page-specific tags if a page subslug is provided
  const getPageTags = () => {
    // For company_data view format, tags are already in the company object
    // We just need to filter them based on page_id

    // For the main page or no subslug specified
    if (!pageSubslug || pageSubslug === 'main') {
      // Filter company-level tags (those without page_id)
      const companyLevelTags = companyTags.filter(tag => !tag.page_id);

      // If we have company-level tags, use those
      if (companyLevelTags.length > 0) {

        return companyLevelTags;
      }

      // If no company-level tags, find tags for the main page
      if (company.pages && Array.isArray(company.pages)) {
        const mainPage = company.pages.find((p: any) => !p.subslug || p.subslug === '');

        if (mainPage) {
          // Find tags that belong to this page
          const mainPageTags = companyTags.filter(tag => tag.page_id === mainPage.id);

          if (mainPageTags.length > 0) {
            console.log(`🔍 [CompanyTags] Using ${mainPageTags.length} tags from main page`);
            return mainPageTags;
          }
        }
      }

      // console.log(`🔍 [CompanyTags] No tags found for main page`);
      return [];
    }

    // For specific page, find the page in the pages array
    if (company.pages && Array.isArray(company.pages)) {
      const page = company.pages.find((p: any) => (p.subslug || '') === pageSubslug);

      if (page) {
        // Find tags that belong to this page
        const pageTags = companyTags.filter(tag => tag.page_id === page.id);

        if (pageTags.length > 0) {
          console.log(`🔍 [CompanyTags] Using ${pageTags.length} tags from page ${pageSubslug}`);
          return pageTags;
        } else {
          console.log(`🔍 [CompanyTags] Page ${pageSubslug} found but has no tags`);
        }
      } else {
        console.log(`🔍 [CompanyTags] Page ${pageSubslug} not found in company pages`);
      }
    } else {
      console.log(`🔍 [CompanyTags] No pages array found in company data`);
    }

    // If no page-specific tags found, return company-level tags as fallback
    if (companyTags && Array.isArray(companyTags) && companyTags.length > 0) {
      console.log(`🔍 [CompanyTags] Falling back to ${companyTags.length} company-level tags`);
      return companyTags; // Already normalized
    }

    console.log(`🔍 [CompanyTags] No tags found for page ${pageSubslug}`);
    return [];
  };

  // Get the tags to display based on the current page
  const tagsToDisplay = getPageTags();

  // This function is now replaced by TaskButton component

  // These functions are now replaced by TaskButton components

  // Reference to store the refresh timeout ID
  const refreshTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Reference to track if a refresh is already in progress
  const isRefreshingRef = React.useRef<boolean>(false);

  // Function to poll job status
  const pollJobStatus = async (jobId: string) => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', jobId)
        .single();

      if (error) {
        console.error('Error polling task status:', error);
        return;
      }

      // Convert task status to job status format
      const taskStatus = {
        id: data.id,
        slug: data.slug,
        job_type: data.type,
        status: data.status,
        timestamp: data.created_at, // Use created_at as timestamp if updated_at doesn't exist
        created_at: data.created_at,
        result: data.result
      };

      setJobStatus(taskStatus);

      // If task is still running, poll again after 2 seconds
      if (data.status === 'pending' || data.status === 'running') {
        setTimeout(() => pollJobStatus(jobId), 2000);
      } else {
        // Task completed or failed
        if (data.status === 'done') {
          // Determine the task type for the success message
          const taskTypeLabel = data.type === 'EXTRACT_METRICS' ? 'Metrics' : 'Tags';

          toast({
            title: `${taskTypeLabel} Extraction Complete`,
            description: `Task completed successfully.`,
          });

          try {
            // Clear any existing refresh timeout to prevent multiple refreshes
            if (refreshTimeoutRef.current) {
              clearTimeout(refreshTimeoutRef.current);
              refreshTimeoutRef.current = null;
            }

            // Single refresh attempt after task completion
            toast({
              title: `${taskTypeLabel} Extraction Complete`,
              description: "Refreshing data...",
            });

            console.log(`🔄 [CompanyTags] Making a single refresh attempt after task completion`);

            // Make a single refresh attempt with a slight delay to allow database to update
            // Only set up a refresh if one isn't already in progress
            if (!isRefreshingRef.current) {
              isRefreshingRef.current = true;

              refreshTimeoutRef.current = setTimeout(async () => {
                try {
                  // Check again before executing to prevent race conditions
                  if (isRefreshingRef.current) {
                    await fetchLatestCompanyData();
                    console.log(`✅ [CompanyTags] Data refreshed successfully after task completion`);
                  } else {
                    console.log(`ℹ️ [CompanyTags] Refresh skipped - another refresh is already in progress`);
                  }
                } catch (error) {
                  console.error(`❌ [CompanyTags] Error in refresh attempt:`, error);

                  // If the first attempt fails, offer a manual refresh option
                  toast({
                    title: "Data Refresh Status",
                    description: "Initial refresh attempt failed. You may need to manually refresh to see the latest data.",
                    action: (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            isRefreshingRef.current = true;
                            fetchLatestCompanyData().finally(() => {
                              isRefreshingRef.current = false;
                            });
                          }}
                        >
                          Refresh Now
                        </Button>
                      </div>
                    ),
                  });
                } finally {
                  // Clear the references after refresh completes (success or failure)
                  refreshTimeoutRef.current = null;
                  isRefreshingRef.current = false;
                }
              }, 2000); // Single 2-second delay before refresh
            } else {
              console.log(`ℹ️ [CompanyTags] Skipping refresh setup - another refresh is already in progress`);
            }
          } catch (refreshError: unknown) {
            console.error(`❌ [CompanyTags] Error refreshing data after task completion:`, refreshError);

            // Get error message safely
            const errorMessage = getErrorMessage(refreshError);

            // If data refresh fails, offer a manual refresh option or reload
            toast({
              title: "Data Refresh Failed",
              description: `Task completed, but data refresh failed: ${errorMessage}. You can try refreshing or reloading the page.`,
              action: (
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      isRefreshingRef.current = true;
                      fetchLatestCompanyData().finally(() => {
                        isRefreshingRef.current = false;
                      });
                    }}
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    Reload Page
                  </Button>
                </div>
              ),
            });
          }
        } else if (data.status === 'failed') {
          // Determine the task type for the error message
          const taskTypeLabel = data.type === 'EXTRACT_METRICS' ? 'Metrics' : 'Tags';

          toast({
            title: `${taskTypeLabel} Extraction Failed`,
            description: data.result?.error || "Unknown error occurred",
            variant: "destructive",
          });
        }
      }
    } catch (pollError: unknown) {
      console.error('Error polling task status:', pollError);

      // Just log the error, no need to show a toast for polling errors
      // as they will be retried automatically
    }
  };

  // Function to fetch the latest task timestamp for a specific task type
  const fetchLatestJobTimestamp = async (taskType: TaskType, setTimestamp: React.Dispatch<React.SetStateAction<string | null>>) => {
    if (!company || !company.slug) return;

    try {
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('created_at')
        .eq('slug', company.slug)
        .eq('type', taskType)
        .order('created_at', { ascending: false })
        .limit(1);

      if (tasksError) {
        console.error(`Error fetching ${taskType} tasks:`, tasksError);
        return;
      }

      if (tasks && tasks.length > 0) {
        setTimestamp(tasks[0].created_at);
      }
    } catch (timestampError: unknown) {
      console.error(`Error fetching ${taskType} task timestamp:`, timestampError);

      // Just log the error, no need to show a toast for timestamp errors
      // as they don't affect the main functionality
    }
  };

  // Function to fetch the latest company data
  const fetchLatestCompanyData = async () => {
    if (!company || !company.slug) {
      console.error('❌ [CompanyTags] Cannot fetch data: Missing company slug');
      return;
    }

    try {
      setIsRefreshingData(true);
      setRefreshAttempts(prev => prev + 1);

      // Log the refresh attempt
      console.log(`🔄 [CompanyTags] Refresh attempt ${refreshAttempts + 1} for ${company.slug}`);

      // We'll handle multiple refresh attempts in the polling mechanism instead
      // of reloading the page after 3 attempts
      console.log(`🔄 [CompanyTags] Fetching latest data for ${company.slug}`);

      // Fetch the latest company data directly from Supabase instead of using the API
      // First, get the basic company data
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('slug', company.slug)
        .single();

      if (companyError) {
        console.error(`❌ [CompanyTags] Error fetching company data:`, companyError);
        throw new Error(`Failed to fetch company data: ${companyError.message}`);
      }

      if (!companyData) {
        throw new Error(`No company found with slug: ${company.slug}`);
      }

      // Then, fetch the tags separately
      console.log(`🔍 [CompanyTags] Fetching tags for company ID: ${companyData.id}`);

      try {
        // First get all versions for this company and sort them
        const { data: allVersions, error: versionsError } = await supabase
          .from('tags_map')
          .select('version')
          .eq('company_id', companyData.id)
          .order('version', { ascending: false });

        if (versionsError) {
          console.error(`❌ [CompanyTags] Error fetching versions:`, versionsError);
          throw new Error(`Failed to fetch versions: ${versionsError.message}`);
        }
        
        // Extract unique versions and sort them in descending order (newest first)
        const uniqueVersions = [...new Set(allVersions?.map(v => v.version) || [])]
          .filter(v => v) // Remove null/undefined versions
          .sort((a, b) => {
            // Sort versions in YYYYMMDD format (descending)
            if (a && b && /^\d{8}$/.test(a) && /^\d{8}$/.test(b)) {
              return parseInt(b) - parseInt(a);
            }
            return String(b).localeCompare(String(a));
          });

        // Store all available versions for potential use in UI
        setAvailableVersions(uniqueVersions);

        // Get the latest version (first in the sorted array)
        const latestVersion = uniqueVersions.length > 0 ? uniqueVersions[0] : null;
        console.log(`✅ [CompanyTags] Latest version identified: ${latestVersion}`);

        // Now fetch tags for the latest version only (or all if showAllVersions is true)
        let query = supabase
          .from('tags_map')
          .select(`
            id,
            tag_id,
            company_id,
            page_id,
            version,
            created_at,
            explanation,
            tag:tags(*)
          `)
          .eq('company_id', companyData.id);
          
        // If not showing all versions and we have a latest version, filter by it
        if (!showAllVersions && latestVersion) {
          query = query.eq('version', latestVersion);
        }
          
        // Execute the query
        const { data: tagMappings, error: tagsError } = await query;

        if (tagsError) {
          console.error(`❌ [CompanyTags] Error fetching tags:`, tagsError);
          throw new Error(`Failed to fetch tags: ${tagsError.message}`);
        }

        console.log(`✅ [CompanyTags] Successfully fetched ${tagMappings?.length || 0} tags for company ID: ${companyData.id}`);

        // Process the tags to match the expected format
        const processedTags = (tagMappings || []).map(tagMapping => {
          return {
            id: tagMapping.id,
            tag_mapping_id: tagMapping.id,
            name: tagMapping.tag?.name || 'Unknown Tag',
            description: tagMapping.tag?.description || '',
            keywords: tagMapping.tag?.keywords || [],
            explanation: tagMapping.explanation || '',
            version: tagMapping.version,
            created_at: tagMapping.created_at
          };
        });

        // Add the processed tags to the company data
        companyData.tags = processedTags;

        console.log(`✅ [CompanyTags] Processed ${processedTags.length} tags for display`);
      } catch (error: any) {
        console.error(`❌ [CompanyTags] Exception while fetching tags:`, error);

        // If there's an error fetching tags, try a simpler query without the tag relationship
        try {
          console.log(`🔄 [CompanyTags] Trying simpler tags query without relationship`);

          const { data: simpleTagMappings, error: simpleTagsError } = await supabase
            .from('tags_map')
            .select('id, tag_id, company_id, page_id, version, created_at, explanation')
            .eq('company_id', companyData.id);

          if (simpleTagsError) {
            console.error(`❌ [CompanyTags] Error with simpler tags query:`, simpleTagsError);
            companyData.tags = [];
          } else {
            console.log(`✅ [CompanyTags] Successfully fetched ${simpleTagMappings?.length || 0} tags with simpler query`);

            // For each tag mapping, fetch the tag separately
            const processedTags = [];

            for (const mapping of (simpleTagMappings || [])) {
              try {
                const { data: tagData } = await supabase
                  .from('tags')
                  .select('*')
                  .eq('id', mapping.tag_id)
                  .single();

                if (tagData) {
                  processedTags.push({
                    id: mapping.id,
                    tag_mapping_id: mapping.id,
                    name: tagData.name || 'Unknown Tag',
                    description: tagData.description || '',
                    keywords: tagData.keywords || [],
                    explanation: mapping.explanation || '',
                    version: mapping.version,
                    created_at: mapping.created_at
                  });
                }
              } catch (tagError: unknown) {
                console.warn(`⚠️ [CompanyTags] Error fetching individual tag:`, tagError);
                // Continue with the next tag
              }
            }

            companyData.tags = processedTags;
            console.log(`✅ [CompanyTags] Processed ${processedTags.length} tags with fallback method`);
          }
        } catch (fallbackError: unknown) {
          console.error(`❌ [CompanyTags] Fallback tags query also failed:`, fallbackError);
          companyData.tags = [];

          // Get error message safely
          const errorMessage = getErrorMessage(fallbackError) ? `: ${getErrorMessage(fallbackError)}` : '';

          // Show a warning toast but continue
          toast({
            title: "Warning",
            description: `Tag data could not be loaded${errorMessage}. Other company data was loaded successfully.`,
            variant: "warning",
          });
        }
      }

      // Optionally fetch pages if needed (not using the relationship)
      try {
        const { data: pages, error: pagesError } = await supabase
          .from('company_pages')
          .select('*')
          .eq('company_id', companyData.id);

        if (!pagesError && pages) {
          companyData.pages = pages;

          // For each page, fetch its tags
          for (const page of pages) {
            try {
              if (page.id) {
                const { data: pageTagMappings, error: pageTagsError } = await supabase
                  .from('tags_map')
                  .select(`
                    id,
                    tag_id,
                    company_id,
                    page_id,
                    version,
                    created_at,
                    explanation,
                    tag:tags(*)
                  `)
                  .eq('company_id', companyData.id)
                  .eq('page_id', page.id);

                if (!pageTagsError && pageTagMappings) {
                  // Process the tags to match the expected format
                  const processedPageTags = pageTagMappings.map(tagMapping => {
                    return {
                      id: tagMapping.id,
                      tag_mapping_id: tagMapping.id,
                      name: tagMapping.tag?.name || 'Unknown Tag',
                      description: tagMapping.tag?.description || '',
                      keywords: tagMapping.tag?.keywords || [],
                      explanation: tagMapping.explanation || '',
                      version: tagMapping.version,
                      created_at: tagMapping.created_at
                    };
                  });

                  // Add the processed tags to the page
                  page.tags = processedPageTags;
                  console.log(`✅ [CompanyTags] Processed ${processedPageTags.length} tags for page ${page.subslug || 'main'}`);
                }
              }
            } catch (pageTagError: unknown) {
              console.warn(`⚠️ [CompanyTags] Error fetching tags for page ${page.subslug || 'main'}:`, pageTagError);
              page.tags = [];
            }
          }
        }
      } catch (pagesError) {
        console.warn(`⚠️ [CompanyTags] Error fetching pages:`, pagesError);
        // Continue even if pages fetch fails
      }

      // Set the updated company data
      console.log(`✅ [CompanyTags] Setting updated company data with ${companyData.tags?.length || 0} tags`);

      // Check if we actually got new tags
      const hadTagsBefore = company.tags?.length > 0;
      const hasTagsNow = companyData.tags?.length > 0;

      // Log the tag count change
      console.log(`🔍 [CompanyTags] Tags before: ${company.tags?.length || 0}, Tags now: ${companyData.tags?.length || 0}`);

      setCompany(companyData);

      // Force a re-render by updating the refresh key
      setRefreshKey(prevKey => prevKey + 1);

      // Reset refresh attempts counter on success
      setRefreshAttempts(0);

      // Refresh timestamps
      fetchLatestJobTimestamp('EXTRACT_TAGS', setLastJobTimestamp);
      fetchLatestJobTimestamp('EXTRACT_METRICS', setLastMetricsJobTimestamp);

      // Show appropriate toast based on whether tags were found
      if (!hadTagsBefore && hasTagsNow) {
        toast({
          title: "Success",
          description: `Found ${companyData.tags?.length} new tags!`,
        });
      } else if (hasTagsNow) {
        toast({
          title: "Success",
          description: `Company data refreshed with ${companyData.tags?.length} tags.`,
        });
      } else {
        toast({
          title: "Data Refreshed",
          description: "Company data refreshed, but no tags were found. The system may still be processing the data.",
          action: (
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchLatestCompanyData}
              >
                Try Again
              </Button>
            </div>
          ),
        });
      }
    } catch (fetchError: unknown) {
      console.error(`❌ [CompanyTags] Error fetching company data:`, fetchError);

      // Get error message safely
      let errorMessage = 'Unknown error';
      if (fetchError instanceof Error) {
        errorMessage = fetchError.message;
      } else if (typeof fetchError === 'string') {
        errorMessage = fetchError;
      } else if (fetchError && typeof fetchError === 'object' && 'message' in fetchError) {
        errorMessage = String((fetchError as { message: unknown }).message);
      } else if (fetchError !== null && fetchError !== undefined) {
        errorMessage = String(fetchError);
      }

      toast({
        title: "Error",
        description: `Failed to refresh company data: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsRefreshingData(false);
    }
  };

  // Function to fetch available validated versions
  const fetchAvailableVersions = () => {
    if (!company || !company.slug) return;

    try {
      console.log(`🔍 [CompanyTags] Fetching available versions for ${company.slug}`);
      
      // Use the getVersions utility function from versionUtils.ts
      // This gets versions from the Redux store which already has the company data
      // Pass undefined to get all versions rather than defaulting to "main"
      const versions = pageSubslug === "main" ? getVersions() : getVersions(pageSubslug);
      
      console.log(`✅ [CompanyTags] Fetched ${versions.length} available versions using getVersions`);
      
      if (versions.length > 0) {
        setAvailableVersions(versions);
        setSelectedExtractVersion(versions[0]);
      } else {
        console.log('No versions found from getVersions, trying fallback methods');
        
        // Fallback 1: Try to get versions from company pages scrapes directly
        if (company.pages && Array.isArray(company.pages)) {
          // Filter pages by subslug if provided
          const relevantPages = pageSubslug
            ? company.pages.filter(page => page.subslug === pageSubslug)
            : company.pages;
            
          const allScrapes = relevantPages.flatMap(p => p.scrapes || []);
          const pageVersions = [...new Set(allScrapes.map(s => s.version).filter(Boolean))];
          
          if (pageVersions.length > 0) {
            console.log(`✅ [CompanyTags] Found ${pageVersions.length} versions from company pages`);
            pageVersions.sort((a, b) => b.localeCompare(a)); // Sort newest first
            setAvailableVersions(pageVersions);
            setSelectedExtractVersion(pageVersions[0]);
            return;
          }
        }
        
        // Fallback 2: If still no versions, try using maps
        if (company.maps && Array.isArray(company.maps)) {
          const mapVersions = [...new Set(company.maps.map(m => m.version).filter(Boolean))];
          
          if (mapVersions.length > 0) {
            console.log(`✅ [CompanyTags] Found ${mapVersions.length} versions from maps`);
            mapVersions.sort((a, b) => b.localeCompare(a)); // Sort newest first
            setAvailableVersions(mapVersions);
            setSelectedExtractVersion(mapVersions[0]);
            return;
          }
        }
        
        // Fallback 3: Use the lastJobTimestamp if available
        if (lastJobTimestamp) {
          console.log(`✅ [CompanyTags] Using lastJobTimestamp as fallback: ${lastJobTimestamp}`);
          setAvailableVersions([lastJobTimestamp]);
          setSelectedExtractVersion(lastJobTimestamp);
          return;
        }
        
        // If still no versions, just use empty array to indicate we'll use the latest
        setAvailableVersions([]);
        setSelectedExtractVersion(null);
      }
    } catch (error) {
      console.error('Exception fetching versions:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while fetching versions",
        variant: "destructive",
      });
      
      // Even if we have an error, we should still allow extraction with no specific version
      setAvailableVersions([]);
      setSelectedExtractVersion(null);
    }
  };

  // Function to run extraction with selected options
  const runExtractWithOptions = async () => {
    if (!company || !company.slug) return;

    try {
      // Close the modal
      setExtractModalOpen(false);
      
      // Prepare extraction parameters
      const extractParams: any = {
        slug: company.slug,
        force: forceExtract,
      };
      
      // Only add version if one is selected
      if (selectedExtractVersion) {
        extractParams.version = selectedExtractVersion;
      }
      
      // Add subslug if present and not "main"
      if (pageSubslug && pageSubslug !== "main") {
        extractParams.subslug = pageSubslug;
      }
      
      console.log(`🚀 [CompanyTags] Running extraction with options:`, extractParams);
      
      // Show loading toast
      toast({
        title: "Starting Extraction",
        description: "Tag extraction task has been initiated.",
      });
      
      // Create and enqueue the extraction task
      const taskResult = await createAndEnqueueTask(
        TaskType.EXTRACT_TAGS,
        company.slug,
        extractParams
      );
      
      if (taskResult?.data?.task_id) {
        console.log(`✅ [CompanyTags] Task created with ID: ${taskResult.data.task_id}`);
        
        // Start polling the job status
        pollJobStatus(taskResult.data.task_id);
        
        toast({
          title: "Extraction Task Created",
          description: "Tag extraction is running in the background. You will be notified when it completes.",
        });
      } else {
        console.error('❌ [CompanyTags] Failed to create task, no task ID returned');
        toast({
          title: "Error",
          description: "Failed to create extraction task",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error running extraction:', error);
      toast({
        title: "Error",
        description: "Failed to run tag extraction",
        variant: "destructive",
      });
    }
  };

  // Fetch the latest job timestamps when the component mounts
  useEffect(() => {
    if (company && company.slug) {
      fetchLatestJobTimestamp('EXTRACT_TAGS', setLastJobTimestamp);
      fetchLatestJobTimestamp('EXTRACT_METRICS', setLastMetricsJobTimestamp);
    }

    // Cleanup function to clear any pending refresh timeouts when component unmounts
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
        console.log('🧹 [CompanyTags] Cleaned up pending refresh timeout on unmount');
      }

      // Reset the refreshing flag
      if (isRefreshingRef.current) {
        isRefreshingRef.current = false;
        console.log('🧹 [CompanyTags] Reset refreshing flag on unmount');
      }
    };
  }, [company]);
  


  // Process tags to ensure keywords are always arrays and handle the new schema
  const processedTags = tagsToDisplay.map((tag: TagItem) => {
    // Create a new object instead of modifying the original
    const processedTag = { ...tag };

    // Process keywords if they're a string
    if (typeof processedTag.keywords === 'string' && processedTag.keywords) {
      // Convert comma-separated string to array
      processedTag.keywords = processedTag.keywords.split(',').map((k: string) => k.trim());
    } else if (!Array.isArray(processedTag.keywords)) {
      // Ensure keywords is always an array
      processedTag.keywords = [];
    }

    // Handle the new schema fields
    if (processedTag.tag_mapping_id) {
      // If tag_mapping_id exists, this is from the new schema
      console.log(`🔍 [CompanyTags] Processing tag with tag_mapping_id: ${processedTag.tag_mapping_id}`);

      // Make sure id is set (use tag_mapping_id if id is missing)
      if (!processedTag.id) {
        processedTag.id = processedTag.tag_mapping_id;
      }
    }

    // Make sure all required fields exist
    if (!processedTag.name) {
      console.warn(`⚠️ [CompanyTags] Tag missing name:`, processedTag);
      processedTag.name = 'Unnamed Tag';
    }

    if (!processedTag.description) {
      processedTag.description = '';
    }

    return processedTag;
  });

  // Remove duplicate tags based on tag ID
  const uniqueTags = Array.from(
    new Map(
      processedTags
        .filter((tag: TagItem) => tag && tag.id) // Filter out undefined or tags without ID
        .map((tag: TagItem) => [tag.id, tag])
    ).values()
  );

  // Find the latest version that has tag data
  useEffect(() => {
    if (uniqueTags.length > 0) {
      // Collect all versions from tags
      const versions = uniqueTags
        .map(tag => tag.version)
        .filter(version => version) as string[];
      
      // If we have versions, find the latest one
      if (versions.length > 0) {
        // Sort in descending order (newest first)
        const sortedVersions = [...versions].sort((a, b) => 
          b.localeCompare(a)
        );
        
        // Log the raw version format
        console.log(`🔍 [CompanyTags] Raw version format: ${sortedVersions[0]}, Type: ${typeof sortedVersions[0]}`);
        
        // Set the latest version
        setLatestVersionWithData(sortedVersions[0]);
        console.log(`✅ [CompanyTags] Latest version with data: ${sortedVersions[0]}`);
      } else {
        console.log(`⚠️ [CompanyTags] No versions found in tags`);
        setLatestVersionWithData(null);
      }
    } else {
      setLatestVersionWithData(null);
    }
  }, [uniqueTags]);
  
  // Filter tags based on version if showAllVersions is false
  const filteredTags = showAllVersions 
    ? uniqueTags 
    : uniqueTags.filter(tag => {
        // If we're not showing all versions, only show tags from the latest version with data
        if (!latestVersionWithData) return true; // Show all tags if we couldn't determine a latest version
        
        return !tag.version || tag.version === latestVersionWithData;
      });

  // Sort tags alphabetically by name
  const sortedTags = [...filteredTags].sort((a, b) => a.name.localeCompare(b.name));
  
  // Log the filtering results for debugging
  useEffect(() => {
    if (!showAllVersions) {
      console.log(`🔍 [CompanyTags] Filtering to ${filteredTags.length} tags from version ${latestVersionWithData || 'unknown'}`);
    } else {
      console.log(`🔍 [CompanyTags] Showing all ${uniqueTags.length} tags across all versions`);
    }
  }, [filteredTags, uniqueTags, showAllVersions, latestVersionWithData]);

  // Use the refreshKey in the key of the main component to force re-render
  return (
    <div className="space-y-6" key={`company-tags-${refreshKey}`}>
      {isRefreshingData && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 text-blue-700">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Refreshing company data... This may take a moment as the database updates.</span>
          </div>
          <div className="text-xs mt-1">
            Refresh attempt: {refreshAttempts}
          </div>
        </div>
      )}

      {/* Version Action Bar */}
      {/* <VersionActionBar
        actualVersion={lastJobTimestamp ? new Date(lastJobTimestamp).toLocaleDateString() : null}
        globalVersion={null}
        subslug={pageSubslug || undefined}
        taskType={TaskType.EXTRACT_TAGS}
        buttonText="Extract Tags"
        icon={<Tag className="h-4 w-4 mr-2" />}
        onTaskComplete={(result) => {
          toast({
            title: "Tags Extracted",
            description: "Tags have been extracted successfully. Refreshing data...",
          });
          fetchLatestCompanyData();
        }}
      /> */}

      <div className="flex justify-between items-center mt-6">
        <div>
          <h2 className="text-xl font-semibold">
            Company Tags
            {!showAllVersions && availableVersions.length > 0 && (
              <span className="text-sm text-gray-500 font-normal ml-2">
                [Version: {availableVersions[0]}]
              </span>
            )}
          </h2>
        </div>
      </div>

      {/* Header with Version Toggle and Action Buttons on the same line */}
      <div className="flex justify-between items-center mb-4">
        {/* Toggle for showing all versions */}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-all-versions"
            checked={!showAllVersions}
            onCheckedChange={(checked) => {
              setShowAllVersions(!checked);
              // Force a re-render by updating the refresh key
              setRefreshKey(prevKey => prevKey + 1);
            }}
          />
          <Label htmlFor="show-all-versions" className="text-sm">
            {!showAllVersions 
              ? `Show Current Tags Only ${latestVersionWithData ? `(${formatVersionDate(latestVersionWithData)})` : lastJobTimestamp ? `(${new Date(lastJobTimestamp).toLocaleDateString()})` : ''}` 
              : 'Show All Tags'}
          </Label>
        </div>
        
        <div className="flex items-center gap-2">
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Fetch available versions before opening modal
                    fetchAvailableVersions();
                    setExtractModalOpen(true);
                  }}
                >
                  <PlayCircle className="h-4 w-4 mr-2" /> Run Extract
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Extract tags from the pricing page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Dialog 
            open={extractModalOpen} 
            onOpenChange={setExtractModalOpen}
            onKeyDown={(e) => {
              // Prevent escape key from propagating
              if (e.key === 'Escape') {
                e.stopPropagation();
                setExtractModalOpen(false);
              }
            }}
          >
            <DialogContent 
              className="sm:max-w-md"
              onEscapeKeyDown={(e) => {
                // Prevent escape key from propagating
                e.preventDefault();
                e.stopPropagation();
                setExtractModalOpen(false);
              }}
            >
              <DialogHeader>
                <DialogTitle>Extract Tags</DialogTitle>
                <DialogDescription>
                  Select a version to extract tags from and configure options.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="version-select">Version:</Label>
                  <select 
                    id="version-select"
                    className="w-full p-2 border rounded-md text-sm"
                    value={selectedExtractVersion || ''}
                    onChange={(e) => setSelectedExtractVersion(e.target.value || null)}
                  >
                    <option value="">Latest Version</option>
                    {availableVersions.length > 0 ? (
                      availableVersions.map(version => (
                        <option key={version} value={version}>
                          {formatVersionDate(version)}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No specific versions available</option>
                    )}
                  </select>
                  {availableVersions.length === 0 && (
                    <p className="text-xs text-yellow-600">
                      No specific versions found. The latest version will be used.
                    </p>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="force-extract"
                      checked={forceExtract}
                      onCheckedChange={(checked) => setForceExtract(checked === true)}
                    />
                    <Label htmlFor="force-extract" className="text-sm font-medium cursor-pointer">
                      Force re-extract (ignore cache)
                    </Label>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setExtractModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={runExtractWithOptions}
                >
                  Extract Tags
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isRefreshingData && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Refreshing company data...</span>
          </div>
        </div>
      )}

      {sortedTags.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center text-gray-500">
          <Tag className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="mb-4">No tags have been extracted for this company yet.</p>

          {jobStatus?.status === 'done' && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 text-sm">
              <p><strong>Note:</strong> If you just ran an extraction, there might be a delay before tags appear.</p>
              <p className="mt-1">Try clicking "Refresh Data" below after a few moments.</p>
            </div>
          )}

          <div className="flex justify-center">
            <TaskButton
              taskType={TaskType.EXTRACT_TAGS}
              buttonText="Extract Tags Now"
              onTaskComplete={(result) => {
                toast({
                  title: "Tags Extracted",
                  description: "Tags have been extracted successfully. Refreshing data...",
                });
                fetchLatestCompanyData();
              }}
              className="w-auto"
            />
          </div>

          {lastJobTimestamp && (
            <p className="mt-4 text-xs text-gray-400">
              Last extraction attempt: {new Date(lastJobTimestamp).toLocaleString()}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {sortedTags.map((tag: TagItem, index: number) => {
              // Create a deterministic but varied color for each tag
              // Using a simple hash function based on the tag name
              const tagColors = [
                "bg-blue-100 text-blue-800 hover:bg-blue-200",
                "bg-green-100 text-green-800 hover:bg-green-200",
                "bg-purple-100 text-purple-800 hover:bg-purple-200",
                "bg-pink-100 text-pink-800 hover:bg-pink-200",
                "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
                "bg-indigo-100 text-indigo-800 hover:bg-indigo-200",
                "bg-red-100 text-red-800 hover:bg-red-200",
                "bg-orange-100 text-orange-800 hover:bg-orange-200",
                "bg-teal-100 text-teal-800 hover:bg-teal-200",
                "bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
              ];
              
              // Simple hash function to get a consistent color for the same tag name
              const colorIndex = Math.abs(tag.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % tagColors.length;
              const tagColorClass = tagColors[colorIndex];
              
              return (
                <Dialog 
                  key={tag.id} 
                  onOpenChange={(open) => {
                    // If the dialog is closing, prevent event propagation
                    if (!open) {
                      // This will run when the dialog is closing
                      setTimeout(() => {
                        // Focus back on the trigger to prevent navigation
                        document.getElementById(`tag-pill-${tag.id}`)?.focus();
                      }, 0);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent escape key from propagating
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Badge 
                      id={`tag-pill-${tag.id}`}
                      className={`cursor-pointer px-3 py-1.5 text-sm font-medium border-transparent ${tagColorClass}`}
                      onClick={(e) => {
                        // Prevent event bubbling
                        e.stopPropagation();
                      }}
                    >
                      {tag.name}
                    </Badge>
                  </DialogTrigger>
                  <DialogContent 
                    className="sm:max-w-md" 
                    onEscapeKeyDown={(e) => {
                      // Prevent the escape key event from propagating and prevent default behavior
                      e.preventDefault();
                      e.stopPropagation();
                  }}>
                    <DialogHeader>
                      <DialogTitle>{tag.name}</DialogTitle>
                      {tag.description && (
                        <DialogDescription>
                          {tag.description}
                        </DialogDescription>
                      )}
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      {/* Tag explanation */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Explanation:</h4>
                        {tag.explanation ? (
                          <p className="text-sm text-gray-700">{tag.explanation}</p>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No explanation provided</p>
                        )}
                      </div>
                      
                      {/* Keywords */}
                      {tag.keywords && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm">Keywords:</h4>
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(tag.keywords)
                              ? tag.keywords.map((keyword, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {keyword}
                                  </Badge>
                                ))
                              : typeof tag.keywords === 'string' && tag.keywords
                                ? tag.keywords.split(',').map((keyword, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {keyword.trim()}
                                    </Badge>
                                  ))
                                : null
                            }
                          </div>
                        </div>
                      )}
                      
                      {/* Source information */}
                      {tag.sources && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm">Source:</h4>
                          {tag.sources.url && (
                            <p className="text-xs text-blue-500 underline">
                              <a href={tag.sources.url} target="_blank" rel="noopener noreferrer">
                                {tag.sources.url.length > 40
                                  ? tag.sources.url.substring(0, 40) + '...'
                                  : tag.sources.url}
                              </a>
                            </p>
                          )}
                          {tag.sources.text && (
                            <p className="text-xs italic">
                              "{tag.sources.text.length > 100
                                ? tag.sources.text.substring(0, 100) + '...'
                                : tag.sources.text}"
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Version and date information */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        {(tag.sources?.version || tag.version) && (
                          <p className="text-xs text-gray-500">
                            Version: {tag.sources?.version || tag.version}
                          </p>
                        )}
                        {tag.created_at && (
                          <p className="text-xs text-gray-500">
                            Added: {new Date(tag.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      
                      {/* ID information for debugging */}
                      <div className="text-xs text-gray-400 pt-1 border-t border-gray-100">
                        <p>Tag ID: {tag.id}</p>
                        {tag.tag_mapping_id && tag.tag_mapping_id !== tag.id && (
                          <p>Mapping ID: {tag.tag_mapping_id}</p>
                        )}
                      </div>
                    </div>
                    
                    <DialogFooter className="sm:justify-between w-full">
                      <Button 
                        type="button" 
                        variant="destructive"
                        size="sm"
                        onClick={async (e) => {
                          // Prevent event bubbling
                          e.stopPropagation();
                          
                          // Confirm before removing
                          if (window.confirm(`Are you sure you want to remove the tag "${tag.name}"?`)) {
                            try {
                              // Show loading state
                              toast({
                                title: "Removing tag...",
                                description: `Removing "${tag.name}" tag from company.`,
                              });
                              
                              // Use the tag_mapping_id if available, otherwise use the tag id
                              const mappingId = tag.tag_mapping_id || tag.id;
                              
                              // Get the company slug
                              const companySlug = company?.slug;
                              
                              if (!companySlug) {
                                throw new Error("Company slug is required to remove a tag");
                              }
                              
                              // Call the helper function to remove the tag
                              const result = await removeTagFromCompany(supabase, mappingId, companySlug);
                              
                              if (result.success) {
                                toast({
                                  title: "Tag Removed",
                                  description: `Successfully removed "${tag.name}" tag.`,
                                });
                                
                                // Refresh the data to update the UI
                                fetchLatestCompanyData();
                                
                                // Close the dialog
                                document.querySelector('[data-state="open"] [aria-label="Close"]')?.dispatchEvent(
                                  new MouseEvent('click', { bubbles: true })
                                );
                              } else {
                                toast({
                                  title: "Error",
                                  description: `Failed to remove tag: ${result.error}`,
                                  variant: "destructive",
                                });
                              }
                            } catch (error: any) {
                              console.error('Error removing tag:', error);
                              toast({
                                title: "Error",
                                description: `An unexpected error occurred: ${error.message}`,
                                variant: "destructive",
                              });
                            }
                          }
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Tag
                      </Button>
                      
                      <DialogClose asChild>
                        <Button 
                          type="button" 
                          variant="secondary"
                          onClick={(e) => {
                            // Prevent event bubbling
                            e.stopPropagation();
                          }}
                        >
                          Close
                        </Button>
                      </DialogClose>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyTags;
